import * as d3 from 'd3';
import { BaseChartController } from '../../../utils/helpers/BaseChartController';
import { Color } from '@ionic/core';

export interface PieChartDataEntry {
  /** A unique identifier for this section of the pie chart. */
  key: string;
  /** The displayed label of the data entry. */
  label: string;
  /** The ionic theme color associated with this entry. */
  color: Color;
  /** The value of the data entry. */
  value: number;
}

const TWO_TIMES_PI = 2 * Math.PI;
/**
 * A class that handles rendering a pie chart in an SVG ref.
 * Inspired by: https://observablehq.com/@6e7ec3b8b5e4f784/simple-3d-pie-chart
 */
export class PieChartController extends BaseChartController {
  private readonly x = 67.5;
  private readonly y = 52;
  private readonly radius = 65;
  private readonly edgeHeight = 12;
  private readonly tilt = 50;

  constructor(
    svg: SVGSVGElement,
    private readonly showTimeDetails: boolean,
  ) {
    super(svg);
    this.svg.attr('viewBox', '0 0 135 115');
    this.svg.selectChildren().remove();
    this.svg.append('g').attr('data-id', 'pie-chart-root');
  }

  public readonly setData = (data: PieChartDataEntry[]) => {
    // Tells D3 Pie arc calculator to look at value prop of PieChartDataEntry
    const pie = d3.pie<PieChartDataEntry>().value((d: any) => d.value);
    // Calculate arcs for data, this also keeps the extra props (label, color), intact on each entry.
    const _data = pie(data);

    const slices = this.svg
      .select('[data-id=pie-chart-root]')
      .attr('transform', 'translate(' + this.x + ',' + this.y + ')')
      .attr('class', 'slices');

    // Handles rendering the edge of the pie chart
    slices
      .selectAll('.outer-slice')
      .data(_data)
      .join('path')
      .classed('outer-slice', true)
      .attr('d', d => pieOuter(d, this.radius - 0.5, this.tilt - 0.5, this.edgeHeight))
      .attr('opacity', 1);

    // Handles rendering the top of the pie chart.
    slices
      .selectAll('.top-slice')
      .data(_data)
      .join('path')
      .classed('top-slice', true)
      .attr('d', d => pieTop(d, this.radius, this.tilt))
      .attr('opacity', 1);

    slices
      .selectAll<SVGPathElement, d3.PieArcDatum<PieChartDataEntry>>('path')
      .classed('ion-color-data-green', d => d.data.color === 'data-green')
      .classed('ion-color-data-blue', d => d.data.color === 'data-blue')
      .classed('ion-color-data-orange', d => d.data.color === 'data-orange')
      .classed('ion-color-data-yellow', d => d.data.color === 'data-yellow')
      .classed('ion-color-data-purple', d => d.data.color === 'data-purple')
      .classed('ion-color-data-teal', d => d.data.color === 'data-teal');
  };
}

/** Calculations for rendering the top of the pie chart correctly. */
function pieTop(d: { endAngle: number; startAngle: number }, rx: number, ry: number) {
  // If angles are equal, then we got nothing to draw
  if (d.endAngle - d.startAngle === 0) return 'M 0 0';

  // Sometimes the D3 pie chart arc calculator above returns an end angle > 2π, which throws off the SVG drawing. Note
  // that this only occurs when all the features but one are zero (e.g. high pressure on and controller off zero but
  // high pressure off not zero for High Pressure pie chart). To address this, we ensure that the end angle is never > 2π.
  const validEndAngle = Math.min(d.endAngle, TWO_TIMES_PI);

  // Calculating shape key points
  const sx = rx * Math.cos(d.startAngle);
  const sy = ry * Math.sin(d.startAngle);
  const ex = rx * Math.cos(validEndAngle);
  const ey = ry * Math.sin(validEndAngle);

  return `M ${sx} ${sy} A ${rx} ${ry} 0 ${validEndAngle - d.startAngle > Math.PI ? 1 : 0} 1 ${ex} ${ey} L 0 0`;
}

/** Calculations for rendering the "side" of the 3D pie chart. */
function pieOuter(d: { endAngle: number; startAngle: number }, rx: number, ry: number, h: number) {
  // Process corner Cases
  if (d.endAngle - d.startAngle === 0) return 'M 0 0';
  if (d.endAngle === Math.PI * 2 && d.startAngle > Math.PI && d.startAngle < Math.PI * 2) {
    return '';
  }

  if (
    d.startAngle > Math.PI * 3 &&
    d.startAngle < Math.PI * 4 &&
    d.endAngle > Math.PI * 3 &&
    d.endAngle <= Math.PI * 4
  ) {
    return '';
  }

  // Reassign startAngle  and endAngle based on their positions
  let startAngle = d.startAngle;
  let endAngle = d.endAngle;
  if (d.startAngle > Math.PI && d.startAngle < Math.PI * 2) {
    startAngle = Math.PI;
    if (d.endAngle > Math.PI * 2) {
      startAngle = 0;
    }
  }
  if (d.endAngle > Math.PI && d.endAngle < Math.PI * 2) {
    endAngle = Math.PI;
  }
  if (d.startAngle > Math.PI * 2) {
    startAngle = d.startAngle % (Math.PI * 2);
  }
  if (d.endAngle > Math.PI * 2) {
    endAngle = d.endAngle % (Math.PI * 2);
    if (d.startAngle <= Math.PI) {
      endAngle = Math.PI;
      startAngle = 0;
    }
  }
  if (d.endAngle > Math.PI * 3) {
    endAngle = Math.PI;
  }
  if (d.startAngle < Math.PI && d.endAngle >= 2 * Math.PI) {
    endAngle = Math.PI;
    startAngle = d.startAngle;
  }

  // Calculating shape key points
  const sx = rx * Math.cos(startAngle);
  const sy = ry * Math.sin(startAngle);
  const ex = rx * Math.cos(endAngle);
  const ey = ry * Math.sin(endAngle);

  // Creating custom path  commands based on calculation
  return `M ${sx} ${h + sy} A ${rx} ${ry} 0 0 1 ${ex} ${h + ey} L ${ex} ${ey} A ${rx} ${ry} 0 0   0 ${sx} ${sy} z`;
}
